import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from '@/lib/utils';
import { useLanguage } from '@/context/LanguageContext';

const cryptoAssets = [
  { symbol: 'BTC', name: 'Bitcoin', price: 67234.56, icon: 'fa-brands fa-bitcoin', color: 'text-crypto-bitcoin', balance: 1.5234 },
  { symbol: 'ETH', name: 'Ethereum', price: 3245.78, icon: 'fa-brands fa-ethereum', color: 'text-crypto-ethereum', balance: 12.456 },
  { symbol: 'SOL', name: 'Solana', price: 142.56, icon: 'fa-solid fa-sun', color: 'text-purple-500', balance: 89.23 },
  { symbol: 'USDT', name: 'Tether', price: 1.00, icon: 'fa-solid fa-dollar-sign', color: 'text-green-500', balance: 5420.00 },
];

const recentSwaps = [
  { id: 1, from: 'BTC', to: 'ETH', fromAmount: 0.5, toAmount: 10.35, time: '2h', status: 'completed' },
  { id: 2, from: 'ETH', to: 'USDT', fromAmount: 5.0, toAmount: 16228.90, time: '5h', status: 'completed' },
  { id: 3, from: 'SOL', to: 'BTC', fromAmount: 100, toAmount: 0.212, time: '1d', status: 'completed' },
];

export const Exchange = () => {
  const { t } = useLanguage();
  const [fromAsset, setFromAsset] = useState('BTC');
  const [toAsset, setToAsset] = useState('ETH');
  const [fromAmount, setFromAmount] = useState('');
  const [orderType, setOrderType] = useState('market');

  const fromCrypto = cryptoAssets.find(c => c.symbol === fromAsset);
  const toCrypto = cryptoAssets.find(c => c.symbol === toAsset);
  
  const exchangeRate = fromCrypto && toCrypto ? fromCrypto.price / toCrypto.price : 0;
  const toAmount = fromAmount ? (parseFloat(fromAmount) * exchangeRate).toFixed(6) : '';

  const handleSwapAssets = () => {
    const temp = fromAsset;
    setFromAsset(toAsset);
    setToAsset(temp);
    setFromAmount('');
  };

  return (
    <div className="p-4 lg:p-6 space-y-6 animate-fade-in" data-testid="exchange-page">
      {/* Page Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-semibold">{t('exchange')}</h1>
        <p className="text-sm text-muted-foreground mt-1">{t('swapInstantly')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Exchange Card */}
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">{t('swap')}</CardTitle>
                <Tabs value={orderType} onValueChange={setOrderType}>
                  <TabsList className="h-8">
                    <TabsTrigger value="market" className="text-xs px-3 h-7">{t('market')}</TabsTrigger>
                    <TabsTrigger value="limit" className="text-xs px-3 h-7">{t('limit')}</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* From Section */}
              <div className="p-4 rounded-lg bg-muted/50 border border-border">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm text-muted-foreground">{t('from')}</Label>
                  <span className="text-xs text-muted-foreground">
                    {t('balance')}: {fromCrypto?.balance.toLocaleString()} {fromAsset}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={fromAsset} onValueChange={setFromAsset}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {cryptoAssets.map(crypto => (
                        <SelectItem key={crypto.symbol} value={crypto.symbol}>
                          <div className="flex items-center gap-2">
                            <i className={cn(crypto.icon, crypto.color, "text-sm")}></i>
                            {crypto.symbol}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="number"
                    placeholder="0.00"
                    value={fromAmount}
                    onChange={(e) => setFromAmount(e.target.value)}
                    className="flex-1 text-right text-lg font-mono"
                  />
                </div>
                <div className="flex justify-end mt-2">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-xs h-6 px-2"
                    onClick={() => setFromAmount(fromCrypto?.balance.toString() || '')}
                  >
                    {t('max')}
                  </Button>
                </div>
              </div>

              {/* Swap Button */}
              <div className="flex justify-center -my-2 relative z-10">
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-full h-10 w-10 bg-background"
                  onClick={handleSwapAssets}
                >
                  <i className="fa-solid fa-arrow-down-arrow-up"></i>
                </Button>
              </div>

              {/* To Section */}
              <div className="p-4 rounded-lg bg-muted/50 border border-border">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm text-muted-foreground">{t('to')}</Label>
                  <span className="text-xs text-muted-foreground">
                    {t('balance')}: {toCrypto?.balance.toLocaleString()} {toAsset}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={toAsset} onValueChange={setToAsset}>
                    <SelectTrigger className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {cryptoAssets.filter(c => c.symbol !== fromAsset).map(crypto => (
                        <SelectItem key={crypto.symbol} value={crypto.symbol}>
                          <div className="flex items-center gap-2">
                            <i className={cn(crypto.icon, crypto.color, "text-sm")}></i>
                            {crypto.symbol}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    type="text"
                    placeholder="0.00"
                    value={toAmount}
                    readOnly
                    className="flex-1 text-right text-lg font-mono bg-transparent"
                  />
                </div>
              </div>

              {/* Exchange Rate */}
              <div className="p-3 rounded-lg bg-primary/5 border border-primary/10">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">{t('exchangeRate')}</span>
                  <span className="font-mono">
                    1 {fromAsset} = {exchangeRate.toFixed(6)} {toAsset}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm mt-1">
                  <span className="text-muted-foreground">{t('networkFee')}</span>
                  <span className="font-mono text-muted-foreground">~$2.50</span>
                </div>
              </div>

              {/* Swap Button */}
              <Button className="w-full h-12 text-base" size="lg" disabled={!fromAmount}>
                <i className="fa-solid fa-arrow-right-arrow-left mr-2"></i>
                {t('swap')} {fromAsset} → {toAsset}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Your Assets */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('yourAssets')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {cryptoAssets.map(crypto => (
                <div key={crypto.symbol} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={cn("w-8 h-8 rounded-full flex items-center justify-center", 
                      crypto.symbol === 'BTC' ? 'bg-crypto-bitcoin/15' :
                      crypto.symbol === 'ETH' ? 'bg-crypto-ethereum/15' :
                      crypto.symbol === 'SOL' ? 'bg-purple-500/15' : 'bg-green-500/15'
                    )}>
                      <i className={cn(crypto.icon, crypto.color, "text-sm")}></i>
                    </div>
                    <div>
                      <p className="font-medium text-sm">{crypto.symbol}</p>
                      <p className="text-xs text-muted-foreground">{crypto.name}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm">{crypto.balance.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground font-mono">
                      ${(crypto.balance * crypto.price).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          {/* Recent Swaps */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">{t('recentSwaps')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {recentSwaps.map(swap => (
                <div key={swap.id} className="flex items-center justify-between p-2 rounded-lg border border-border">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <i className="fa-solid fa-arrow-right-arrow-left text-primary text-xs"></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium">{swap.from} → {swap.to}</p>
                      <p className="text-xs text-muted-foreground">{swap.time}</p>
                    </div>
                  </div>
                  <Badge variant="success" className="text-xs">
                    <i className="fa-solid fa-check mr-1"></i>
                    {t('done')}
                  </Badge>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Exchange;
